import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, CalendarDays, Zap,
  FolderKanban, TrendingUp, LayoutGrid, ChevronDown, FolderOpen,
} from "lucide-react";
import { getProjectsOverview } from "../../services/usageService";
import "../../assets/css/ProjectList.css";
import "../../assets/css/Urls.css";

const AVATAR_COLORS = [
  { bg: "var(--primaryTint)", color: "var(--primaryColor)" },
  { bg: "#f0fdf4", color: "#166534" },
  { bg: "#fdf4ff", color: "#7e22ce" },
  { bg: "#fff7ed", color: "#c2410c" },
  { bg: "#ede9fe", color: "#6d28d9" },
];

const ITEMS_PER_PAGE = 20;

const StatCard = ({ icon: Icon, iconBg, iconColor, label, value, sub }) => (
  <div style={{
    background: "#fff", border: "1px solid var(--borderColor)", borderRadius: 12,
    padding: "18px 20px", display: "flex", alignItems: "center", gap: 16,
    boxShadow: "0 1px 3px rgba(15,23,42,.05), 0 4px 12px rgba(15,23,42,.04)", width: "100%",
  }}>
    <div style={{
      width: 48, height: 48, borderRadius: 12, background: iconBg, color: iconColor,
      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    }}>
      <Icon size={22} />
    </div>
    <div style={{ minWidth: 0 }}>
      <p style={{ margin: 0, fontSize: 12, color: "var(--muteColor)", fontWeight: 500, marginBottom: 2 }}>{label}</p>
      <h5 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#0f172a", lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</h5>
      {sub && <p style={{ margin: "4px 0 0", fontSize: 11, color: "var(--muteColor)" }}>{sub}</p>}
    </div>
  </div>
);

const formatDate = (iso) => {
  const d = new Date(iso);
  const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: true })
    .replace("am", "AM").replace("pm", "PM");
  return {
    date: d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
    time,
  };
};

const getPaginationItems = (cur, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (cur <= 4)          return [1, 2, 3, 4, 5, "e1", total];
  if (cur >= total - 3)  return [1, "e0", total - 4, total - 3, total - 2, total - 1, total];
  return [1, "e0", cur - 1, cur, cur + 1, "e1", total];
};

const ProjectHistory = () => {
  const navigate = useNavigate();

  const [items, setItems]               = useState([]);
  const [totalCount, setTotalCount]     = useState(0);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState("");
  const [searchTerm, setSearchTerm]     = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [projectFilter, setProjectFilter] = useState("All");
  const [currentPage, setCurrentPage]   = useState(1);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownPos, setDropdownPos]   = useState({ top: 0, right: 0 });
  const [allProjects, setAllProjects]   = useState([]); // unfiltered, for dropdown + summary stats
  const [allProjectsTotal, setAllProjectsTotal] = useState(0);
  const dropdownRef                     = useRef(null);
  const btnRef                          = useRef(null);
  const searchDebounceRef               = useRef(null);

  // One-time (unfiltered, high-limit) fetch for the dropdown list + summary stat cards
  useEffect(() => {
    getProjectsOverview({ page: 1, limit: 100 })
      .then((res) => {
        setAllProjects(res.data?.response?.items ?? []);
        setAllProjectsTotal(res.data?.response?.total ?? 0);
      })
      .catch(() => {});
  }, []);

  // Debounce the search box — reset to page 1 whenever the query changes
  useEffect(() => {
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(searchDebounceRef.current);
  }, [searchTerm]);

  // Server-side paginated + searched table data
  useEffect(() => {
    setLoading(true);
    setError("");
    getProjectsOverview({ page: currentPage, limit: ITEMS_PER_PAGE, search: debouncedSearch })
      .then((res) => {
        setItems(res.data?.response?.items ?? []);
        setTotalCount(res.data?.response?.total ?? 0);
      })
      .catch(() => setError("Failed to load usage data."))
      .finally(() => setLoading(false));
  }, [currentPage, debouncedSearch]);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          btnRef.current && !btnRef.current.contains(e.target))
        setDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggleDropdown = () => {
    if (!dropdownOpen && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + window.scrollY + 6, right: window.innerWidth - rect.right });
    }
    setDropdownOpen((o) => !o);
  };

  const totalTokens   = allProjects.reduce((s, r) => s + (r.totalTokens ?? 0), 0);
  const totalRequests = allProjects.reduce((s, r) => s + (r.totalRequests ?? 0), 0);
  const topProject    = allProjects.length
    ? allProjects.reduce((a, b) => ((b.totalTokens ?? 0) > (a.totalTokens ?? 0) ? b : a), allProjects[0])
    : null;

  const totalPages = Math.max(1, Math.ceil(totalCount / ITEMS_PER_PAGE));
  const startIdx   = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginated  = items;

  const handleSearch  = (val) => { setSearchTerm(val); };
  const handleProject = (val, projectName) => {
    setProjectFilter(val);
    // The API only supports name-based search, so selecting a project just searches its exact name.
    setSearchTerm(val === "All" ? "" : (projectName ?? ""));
  };

  const selectedLabel = projectFilter === "All"
    ? "All Projects"
    : allProjects.find((p) => p.projectPid === projectFilter)?.projectName ?? "All Projects";

  return (
    <main className="app-wrapper url-list-page">
      <div className="container-fluid">

        {/* Stat cards */}
        <div className="row g-3 mb-4">
          <div className="col-6 col-xl-3">
            <StatCard icon={FolderKanban} iconBg="var(--primaryTint)" iconColor="var(--primaryColor)"  label="Total Projects"    value={allProjectsTotal}                    sub="All tracked projects" />
          </div>
          <div className="col-6 col-xl-3">
            <StatCard icon={LayoutGrid}   iconBg="#f0fdf4" iconColor="var(--successColor)"  label="Total Requests"    value={totalRequests.toLocaleString()}      sub="Across all projects" />
          </div>
          <div className="col-6 col-xl-3">
            <StatCard icon={Zap}          iconBg="#fdf4ff" iconColor="#7e22ce"              label="Total Token Usage" value={totalTokens.toLocaleString()}        sub="Consumed overall" />
          </div>
          <div className="col-6 col-xl-3">
            <StatCard
              icon={TrendingUp}
              iconBg="#fff7ed"
              iconColor="var(--orangeColor)"
              label="Top Project"
              value={topProject?.projectName ?? "—"}
              sub={topProject ? `${(topProject.totalTokens ?? 0).toLocaleString()} tokens` : "No data"}
            />
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={{ padding: "12px 16px", background: "#fff1f2", border: "1px solid #fecdd3", borderRadius: 8, fontSize: 13, color: "#dc2626", marginBottom: 16 }}>
            {error}
          </div>
        )}

        {/* Toolbar */}
        <section className="url-list-toolbar p-3 mb-3">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", width: "100%" }}>
            <div className="url-search-field" style={{ flex: "1 1 200px", minWidth: 0 }}>
              <i className="fa-solid fa-magnifying-glass"></i>
              <input
                type="search"
                className="form-control"
                placeholder="Search by project name, ID..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>

            {/* Project dropdown */}
            <div style={{ position: "relative", flexShrink: 0 }}>
              <button
                ref={btnRef}
                style={{
                  display: "flex", alignItems: "center", gap: 8, minWidth: 180, maxWidth: "100%",
                  padding: "6px 12px", background: "#fff", border: "1px solid #e2e8f0",
                  borderRadius: 8, fontSize: 14, color: "#212529", cursor: "pointer",
                  height: 42, outline: "none", boxShadow: "none",
                }}
                onClick={toggleDropdown}
              >
                <FolderOpen size={14} style={{ flexShrink: 0, color: "#64748b" }} />
                <span style={{ flex: 1, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {selectedLabel}
                </span>
                <ChevronDown size={13} style={{ flexShrink: 0, color: "#64748b", transition: "transform .2s", transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)" }} />
              </button>

              {dropdownOpen && (
                <div ref={dropdownRef} style={{
                  position: "fixed", top: dropdownPos.top, right: dropdownPos.right, zIndex: 99999,
                  background: "#fff", border: "1px solid #dee2e6", borderRadius: 6,
                  boxShadow: "0 2px 8px rgba(15,23,42,.12)", minWidth: 220, overflow: "hidden",
                }}>
                  {[{ projectPid: "All", projectName: "All Projects" }, ...allProjects].map((p) => {
                    const isActive = projectFilter === p.projectPid;
                    return (
                      <div
                        key={p.projectPid}
                        onMouseDown={() => { handleProject(p.projectPid, p.projectName); setDropdownOpen(false); }}
                        style={{
                          padding: "7px 12px", cursor: "pointer", fontSize: 14,
                          color: isActive ? "#fff" : "#212529",
                          background: isActive ? "var(--primaryColor)" : "transparent",
                        }}
                        onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = "#e8f0fe"; }}
                        onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
                      >
                        {p.projectName}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Table */}
        <section className="url-table-card">

          {/* Card header */}
          <div style={{
            padding: "14px 20px", background: "#fafbfc",
            borderBottom: "1px solid var(--borderColor)",
            display: "flex", alignItems: "center", gap: 10,
          }}>
            <div style={{
              width: 34, height: 34, borderRadius: 9,
              background: "var(--primaryTint)", color: "var(--primaryColor)",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <FolderKanban size={16} />
            </div>
            <div>
              <h6 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Usage History</h6>
              <p style={{ margin: 0, fontSize: 11, color: "var(--muteColor)" }}>
                {totalCount} project{totalCount !== 1 ? "s" : ""} found
              </p>
            </div>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table className="table url-table table-hover align-middle mb-0" style={{ minWidth: 640 }}>
              <thead>
                <tr>
                  <th className="text-center" style={{ width: 48 }}>#</th>
                  <th style={{ width: "28%" }}>Project</th>
                  <th style={{ width: "14%" }}>
                    <span className="d-flex align-items-center gap-1">
                      <LayoutGrid size={12} /> Requests
                    </span>
                  </th>
                  <th style={{ width: "20%" }}>
                    <span className="d-flex align-items-center gap-1">
                      <CalendarDays size={12} /> Last Used
                    </span>
                  </th>
                  <th style={{ width: "16%" }}>
                    <span className="d-flex align-items-center gap-1">
                      <Zap size={12} /> Tokens
                    </span>
                  </th>
                  <th style={{ width: 90 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="text-center text-muted py-5 fs-14">Loading…</td>
                  </tr>
                ) : paginated.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center text-muted py-5 fs-14">No results found.</td>
                  </tr>
                ) : (
                  paginated.map((item, idx) => {
                    const { date, time } = item.lastUsedAt ? formatDate(item.lastUsedAt) : { date: "—", time: "" };
                    const palette = AVATAR_COLORS[idx % AVATAR_COLORS.length];
                    return (
                      <tr
                        key={item.projectPid}
                        className="history-row"
                        onClick={() => navigate(`/usage-history-details/${item.projectPid}`)}

                      >
                        <td className="url-index text-center">{startIdx + idx + 1}</td>

                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                            <div style={{
                              width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
                              background: palette.bg, color: palette.color,
                              display: "flex", alignItems: "center", justifyContent: "center",
                              fontSize: 13, fontWeight: 800,
                            }}>
                              {(item.projectName ?? "?").charAt(0).toUpperCase()}
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <p className="url-page-title mb-0" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.projectName}</p>
                            </div>
                          </div>
                        </td>

                        <td>
                          <span style={{
                            fontSize: 11, fontWeight: 600,
                            background: "#f0fdf4", color: "#166534",
                            borderRadius: 6, padding: "3px 9px", display: "inline-block",
                          }}>
                            {(item.totalRequests ?? 0).toLocaleString()}
                          </span>
                        </td>

                        <td>
                          <div style={{ fontSize: 12, color: "#334155", fontWeight: 500, whiteSpace: "nowrap" }}>{date}</div>
                          <div style={{ fontSize: 11, color: "var(--muteColor)", whiteSpace: "nowrap" }}>{time}</div>
                        </td>

                        <td>
                          <span className="ph-token-badge" style={{ whiteSpace: "nowrap" }}>
                            <Zap size={10} />
                            {(item.totalTokens ?? 0).toLocaleString()}
                          </span>
                        </td>

                        <td>
                          <button
                            className="ph-view-btn"
                            onClick={(e) => { e.stopPropagation(); navigate(`/usage-history-details/${item.projectPid}`); }}
                          >
                            View <ChevronRight size={13} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {!loading && totalCount > 0 && (
            <div className="url-pagination-wrap" style={{ flexWrap: "wrap", gap: 8 }}>
              <p className="url-pagination-summary mb-0">
                Showing {startIdx + 1}–{Math.min(startIdx + ITEMS_PER_PAGE, totalCount)} of {totalCount}
              </p>
              <nav>
                <ul className="url-pagination mb-0">
                  <li>
                    <button className="url-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>
                      <ChevronLeft size={15} />
                    </button>
                  </li>
                  {getPaginationItems(currentPage, totalPages).map((item, i) => (
                    <li key={i}>
                      {typeof item === "string" ? (
                        <span className="url-page-ellipsis">…</span>
                      ) : (
                        <button
                          className={`url-page-btn ${item === currentPage ? "active" : ""}`}
                          onClick={() => setCurrentPage(item)}
                        >
                          {item}
                        </button>
                      )}
                    </li>
                  ))}
                  <li>
                    <button className="url-page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)}>
                      <ChevronRight size={15} />
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </section>
      </div>
    </main>
  );
};

export default ProjectHistory;
